import Controller, { OnBeforeSubmit, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { FormAPI, InputAPI, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScoreTiss_28CreateAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import timeService from '../../../../services/time-service';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { DomainType } from '../../score-types';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import DialogService from '../../../../services/dialog-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { WizardSteps } from '../../../../constants/wizards';

interface ResponseCreateScoreTISS28 {
  acidoseAlcalose?: string;
  attendance?: number;
  cateterArtPerif?: string;
  curativoFrequente?: string;
  curativoRotina?: string;
  diureseAtiva?: string;
  dreno?: string;
  evaluationDate?: string;
  idScoreIndexes: number;
  interForaUti?: string;
  interMultiUti?: string;
  interUnicaUti?: string;
  laboratorio?: string;
  levelAttention?: string;
  medDebitoUrina?: string;
  medicEndovenosa?: string;
  medicaUnica?: string;
  monitPadrao?: string;
  nutEnteral?: string;
  nutParent?: string;
  pessoaFisica?: string;
  pressaoIntracraniana?: string;
  repVolemica?: string;
  ressuscitCardio?: string;
  scMonitAtrioEsquerdo?: string;
  scoreQuantity?: number;
  situation?: string;
  suepVentSupl?: string;
  tecHemofiltracao?: string;
  time?: number;
  tratFuncaoPulmonar?: string;
  updateDate?: string;
  updateNrec?: string;
  username?: string;
  usuarioNREC?: string;
  vasoativaMulti?: string;
  vasoativaUnica?: string;
  ventMecanica?: string;
  viaVenosaCentral?: string;
  viasAerias?: string;
}

@Controller({ component: ScoreTiss_28CreateAliases.SCORE_TISS_28_ADD_LAYOUT, fragment: FragmentNames.SCORE_TISS_28_CREATE })
export default class ScoreTiss28CreateController extends BaseController {

  @Inject(ScoreTiss_28CreateAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreTiss_28CreateAliases.FORM_THREE)
  private formThreeAPI!: FormAPI;

  @Inject(ScoreTiss_28CreateAliases.FORM_ONE)
  private formOneAPI!: FormAPI;

  @Inject(ScoreTiss_28CreateAliases.FORM_TWO)
  private formTwoAPI!: FormAPI;

  @Inject(ScoreTiss_28CreateAliases.WIZARD)
  private wizard!: WizardAPI;

  @OnValidate(ScoreTiss_28CreateAliases.FORM_ONE)
  handleOneValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_ONE, validation.valid);
  }

  @OnValidate(ScoreTiss_28CreateAliases.FORM_TWO)
  handleTwoValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_TWO, validation.valid);
  }

  @OnValidate(ScoreTiss_28CreateAliases.FORM_THREE)
  handleThreeValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_THREE, validation.valid);
  }

  @OnMount({ target: ScoreTiss_28CreateAliases.FORM_ONE })
  handleMountForm(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_TISS_28
  }

  @OnMount({ target: ScoreTiss_28CreateAliases.WIZARD })
  handleFormMount(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
    timeService.getCurrentDateTime()
      .then(date => this.setupDateInputApi.setValue(date))
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-dini-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private releaseTISS28Score(sequenceNumber: number): void {
    this.formOneAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_SCORE_TISS_28, { sequenceNumber })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneAPI.setLoading(false);
      });
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseTISS28Score(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private createNewsTISS28Score(body: Record<string, Value>): void {
    this.formThreeAPI.setLoading(true);
    HttpService.post(Endpoint.POST_CREATE_SCORE_TISS_28, undefined, { body })
      .then(response => {
        const {idScoreIndexes} = response.body as ResponseCreateScoreTISS28;
        this.showModalSubmit(idScoreIndexes as number);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formThreeAPI.setLoading(false));
  }

  @OnBeforeSubmit({ target: ScoreTiss_28CreateAliases.WIZARD })
  handleBeforeSubmit(): void {
    const isCreated = this.context.get('isCreated');
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formDataOne   = this.formOneAPI.getRecord();
    const formDataTwo   = this.formTwoAPI.getRecord();
    const formDataThree = this.formThreeAPI.getRecord();
    const acidoseAlcalose = BooleanUtils.isPositiveValue(formDataTwo.acidosis as boolean);
    const cateterArtPerif = BooleanUtils.isPositiveValue(formDataThree.cateterArtPerif as boolean);
    const curativoFrequente = BooleanUtils.isPositiveValue(formDataOne.curativoFrequente as boolean);
    const curativoRotina = BooleanUtils.isPositiveValue(formDataOne.curativoRotina as boolean);
    const diureseAtiva = BooleanUtils.isPositiveValue(formDataTwo.diureseAtiva as boolean);
    const dreno = BooleanUtils.isPositiveValue(formDataOne.dreno as boolean);
    const interForaUti = BooleanUtils.isPositiveValue(formDataThree.interForaUti as boolean);
    const interMultiUti = BooleanUtils.isPositiveValue(formDataThree.interMultiUti as boolean);
    const interUnicaUti = BooleanUtils.isPositiveValue(formDataThree.interUnicaUti as boolean);
    const laboratorio = BooleanUtils.isPositiveValue(formDataOne.laboratorio as boolean);
    const medDebitoUrina = BooleanUtils.isPositiveValue(formDataTwo.medDebitoUrina as boolean);
    const medicEndovenosa = BooleanUtils.isPositiveValue(formDataOne.medicEndovenosa as boolean);
    const medicaUnica = BooleanUtils.isPositiveValue(formDataOne.medicaUnica as boolean);
    const monitPadrao = BooleanUtils.isPositiveValue(formDataOne.monitPadrao as boolean);
    const nutEnteral = BooleanUtils.isPositiveValue(formDataTwo.nutEnteral as boolean);
    const nutParent = BooleanUtils.isPositiveValue(formDataTwo.nutParent as boolean);
    const pressaoIntracraniana = BooleanUtils.isPositiveValue(formDataTwo.pressaoIntracraniana as boolean);
    const repVolemica = BooleanUtils.isPositiveValue(formDataThree.repVolemica as boolean);
    const ressuscitCardio = BooleanUtils.isPositiveValue(formDataThree.ressuscitCardio as boolean);
    const scMonitAtrioEsquerdo = BooleanUtils.isPositiveValue(formDataThree.scMonitAtrioEsquerdo as boolean);
    const suepVentSupl = BooleanUtils.isPositiveValue(formDataTwo.suepVentSupl as boolean);
    const tecHemofiltracao = BooleanUtils.isPositiveValue(formDataTwo.tecHemofiltracao as boolean);
    const time = formDataOne.time ? String((formDataOne.time as DomainType).valueDomain) : 0;
    const tratFuncaoPulmonar = BooleanUtils.isPositiveValue(formDataTwo.tratFuncaoPulmonar as boolean);
    const vasoativaMulti = BooleanUtils.isPositiveValue(formDataThree.vasoativaMulti as boolean);
    const vasoativaUnica = BooleanUtils.isPositiveValue(formDataThree.vasoativaUnica as boolean);
    const ventMecanica = BooleanUtils.isPositiveValue(formDataTwo.ventMecanica as boolean);
    const viaVenosaCentral = BooleanUtils.isPositiveValue(formDataThree.viaVenosaCentral as boolean);
    const viasAerias = BooleanUtils.isPositiveValue(formDataTwo.viasAerias as boolean);
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const evaluationDate = moment(formDataOne.evaluationDate as string).utc().format(ISO_FULL_DATE_FORMAT)
    const body = {
      acidoseAlcalose,
      attendance: encounterId as number,
      cateterArtPerif,
      curativoFrequente,
      curativoRotina,
      diureseAtiva,
      dreno,
      evaluationDate,
      idScoreIndexes: null,
      interForaUti,
      interMultiUti,
      interUnicaUti,
      laboratorio,
      medDebitoUrina,
      medicaUnica,
      medicEndovenosa,
      monitPadrao,
      nutEnteral,
      nutParent,
      pessoaFisica: user.physicalPersonId,
      pressaoIntracraniana,
      repVolemica,
      ressuscitCardio,
      scMonitAtrioEsquerdo,
      suepVentSupl,
      tecHemofiltracao,
      time,
      tratFuncaoPulmonar,
      vasoativaMulti,
      vasoativaUnica,
      ventMecanica,
      viasAerias,
      viaVenosaCentral,
    } as Record<string, Value>;
    this.createNewsTISS28Score(body);
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  @OnUnmount(ScoreTiss_28CreateAliases.SCORE_TISS_28_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_TISS_28;
  }
}
